/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Typography, Link, Box, Paper, CircularProgress, withStyles } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import { inject } from 'mobx-react';
import { shapes } from '@greenville/framework';
import env from '../../env';
import styles from './AiAdminRedirectStyles';

const AiAdminRedirect = ({ redirectPath: initialRedirectPath, classes, language }) => {
  // Store redirectPath in state to prevent it from being lost on re-render
  const [redirectPath] = useState(() => {
    // Get from prop first, then try sessionStorage, then default
    return initialRedirectPath || window.sessionStorage.getItem('aiAdminRedirectPath') || '/berlin/aistudytool/studio';
  });
  const [countdown, setCountdown] = useState(10);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
    if (countdown === 0 && env.AI_ADMIN_URL) {
      // Clear sessionStorage before redirecting
      window.sessionStorage.removeItem('aiAdminRedirectPath');
      window.location.href = `${env.AI_ADMIN_URL}${redirectPath}`;
    }
    return undefined;
  }, [countdown, redirectPath]);

  const handleImmediateRedirect = () => {
    if (env.AI_ADMIN_URL) {
      // Clear sessionStorage before redirecting
      window.sessionStorage.removeItem('aiAdminRedirectPath');
      window.location.href = `${env.AI_ADMIN_URL}${redirectPath}`;
    }
  };

  const progressValue = ((10 - countdown) / 10) * 100;

  return (
    <Box className={classes.root}>
      <Paper className={classes.paper} elevation={0}>
        <Typography variant="h4" className={classes.title}>
          <FormattedMessage {...language.getText('redirect.REDIRECTING_TO_AI_ADMIN')} />
        </Typography>

        <Box className={classes.progressContainer}>
          <CircularProgress
            variant="determinate"
            value={progressValue}
            size={120}
            thickness={4}
            className={classes.progress}
          />
          <Box className={classes.countdownOverlay}>
            <Typography variant="h5" className={classes.countdownText}>
              {countdown}
            </Typography>
          </Box>
        </Box>

        <Typography variant="body1" className={classes.message}>
          <FormattedMessage
            {...language.getText('redirect.AUTO_REDIRECT_MESSAGE')}
            values={{
              countdown,
              plural: countdown !== 1 ? 's' : ''
            }}
          />
        </Typography>

        <Box className={classes.linkContainer}>
          <Link
            href={`${env.AI_ADMIN_URL}${redirectPath}`}
            onClick={(e) => {
              e.preventDefault();
              handleImmediateRedirect();
            }}
            className={classes.link}
            color="primary"
          >
            <FormattedMessage {...language.getText('redirect.CLICK_TO_REDIRECT_NOW')} />
          </Link>
        </Box>
      </Paper>
    </Box>
  );
};

AiAdminRedirect.propTypes = {
  redirectPath: PropTypes.string.isRequired,
  classes: PropTypes.object.isRequired,
  language: shapes.language.isRequired
};

export default withStyles(styles)(inject('language')(AiAdminRedirect));
